import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Maybe } from "true-myth";
import { format } from "date-fns";

import { trackEvent } from "helpers/analytics";
import { DateFormats } from "helpers/dates";

const FRAME_ID_PREFIX = "frame-";
enum FRAME_STATUS {
  DONE,
  FAILURE,
}

interface IMessageStatus {
  status: FRAME_STATUS;
  id: string;
}

function addActionIframe(url: string, action: string): Promise<void> {
  const frame = document.createElement("iframe");
  frame.id = `${FRAME_ID_PREFIX}${action}`;
  frame.width = "0";
  frame.height = "0";
  frame.src = `/${url}#${action}`;
  document.body.appendChild(frame);

  return new Promise((resolve, reject) => {
    // Wait for DONE message to remove
    const onDone = (message: IMessageStatus) => {
      if (frame.id === message.id) {
        if (message.status === FRAME_STATUS.DONE) {
          removeFrame(frame);
          resolve();
        }
        if (message.status === FRAME_STATUS.FAILURE) {
          reject();
        }
      }
    };

    window.addEventListener(
      "message",
      makeMessageReceiver(frame.contentWindow!, onDone),
      false
    );
  });
}

function removeFrame(frame: HTMLIFrameElement): void {
  if (frame.parentElement) {
    frame.parentElement.removeChild(frame);
  }
}

function makeMessageReceiver(
  validSource: Window,
  callback: (message: IMessageStatus) => void,
  remove: boolean = true
): (message: MessageEvent) => void {
  function receiver({ data, origin, source }: MessageEvent): void {
    if (origin === location.origin && source === validSource) {
      // Message is from a valid source
      callback(data);
      if (remove) {
        window.removeEventListener("message", receiver);
      }
    }
  }
  return receiver;
}

export const HASH_ACTION_REGEX = /(print|pdf)$/;
export enum HASH_ACTIONS {
  PRINT = "print",
  PDF = "pdf",
  NONE = "-",
}

export function printSchedule(publicURL: string) {
  trackEvent("print schedule");
  return addActionIframe(publicURL, HASH_ACTIONS.PRINT);
}

export function pdfSchedule(publicURL: string) {
  trackEvent("download pdf");
  const urlToOpen = `/${publicURL}#${HASH_ACTIONS.PDF}`;
  window.open(urlToOpen, "_blank");
  return Promise.resolve();
}

export function printFrame() {
  // TODO - can we avoid the timeout? This accounts for react render cycle delay
  window.setTimeout(() => {
    window.print();
  }, 5);
  window.setTimeout(() => {
    // After printing, send DONE message to parent
    window.parent.postMessage(
      {
        status: FRAME_STATUS.DONE,
        id: `${FRAME_ID_PREFIX}print`,
      },
      location.origin
    );
  }, 200);
}

export function saveFrame(
  tableElement: HTMLTableElement,
  startDate: Maybe<Date>,
  endDate: Maybe<Date>
) {
  const requiredParamsForFileName = [startDate, endDate];
  const fileName = Maybe.all(...requiredParamsForFileName).match({
    Just: ([startDateForFile, endDateForFile]: Date[]) => {
      // NOTE: Left endDateForFile here in case we decide to use it again later
      // For now, experimenting with this shorter filename
      return `shifts_${format(
        startDateForFile,
        DateFormats.YEAR_MONTH_DAY
      )}.pdf`;
    },
    Nothing: () => {
      return "shifts.pdf";
    },
  });
  // rough calculation based on number of single-sized rows per page
  const SINGLEROWHEIGHT = 7.5 / 16;
  const HEADERROWHEIGHT = SINGLEROWHEIGHT / 2;
  let processingRowIndex = 0;
  let pageNum = 0;
  let rowPlacement = 0.5 + HEADERROWHEIGHT;

  const pdf = new jsPDF("l", "in", "letter");

  const headerRow = tableElement.tHead;
  const headerImageData = html2canvas(headerRow).then(
    (canvas: HTMLCanvasElement) => {
      return canvas.toDataURL("image/jpeg", 0.75);
    }
  );

  const rowCollection = tableElement.rows;

  createPage()
    .then(() => processRow(rowCollection[processingRowIndex]))
    .then(() => pdf.save(fileName));

  function processRow(rowEl: Element) {
    // shifts are ~60px high (plus or minus margins) - rough calculation to estimate how many shifts
    const numShiftsHigh = Math.floor(rowEl.clientHeight / 60);
    // calculate how tall this row should be AND how much lower the next row needs to be
    const currentRowHeight = numShiftsHigh * SINGLEROWHEIGHT;
    return html2canvas(rowEl).then((canvas: HTMLCanvasElement) => {
      const imgData = canvas.toDataURL("image/jpeg", 0.25);
      const currentRowPlacement = rowPlacement;
      pdf.addImage(
        imgData,
        "JPEG",
        0.5,
        currentRowPlacement,
        10,
        currentRowHeight,
        null,
        "FAST"
      );
      processingRowIndex++;
      if (processingRowIndex === rowCollection.length) {
        return;
      }
      // determine placement of next row
      rowPlacement += currentRowHeight;
      // checking to make sure there is room on the page for another row
      const pagePromise = rowPlacement >= 7 ? createPage() : Promise.resolve();
      return pagePromise.then(() =>
        processRow(rowCollection[processingRowIndex])
      );
    });
  }

  function createPage() {
    pageNum++;
    if (pageNum >= 2) {
      pdf.addPage();
      pdf.setPage(pageNum);
    }
    rowPlacement = 0.5 + HEADERROWHEIGHT;
    return headerImageData.then((imgData: string) =>
      pdf.addImage(imgData, "JPG", 0.5, 0.5, 10, HEADERROWHEIGHT, null, "FAST")
    );
  }
}
